import { Injectable, inject } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AsyncJobActions } from '@core/async-job-data';
import { AsyncJobType } from '@core/async-job-types';
import { createEffectFactory } from '@core/ngrx-legacy';

import { JobMatchingPlacementsHttpService } from './job-matching-placements-http.service';
import { JobMatchingPlacementsActions } from './job-matching-placements.store';

@Injectable({
  providedIn: 'root',
})
export class JobMatchingPlacementsEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(JobMatchingPlacementsHttpService);

  public effects = createEffectFactory(JobMatchingPlacementsActions, this.actions$);

  public fetchOne = this.effects(JobMatchingPlacementsActions.fetchOne, ({ payload }) =>
    this.http.fetch$(payload.jobId).pipe(
      map(response => ({
        data: response,
      })),
    ),
  );

  public generate$ = this.effects(JobMatchingPlacementsActions.generate, ({ payload }) =>
    this.http.generate$(payload.jobId).pipe(
      switchMap(response => [
        JobMatchingPlacementsActions.generateSuccess({
          payload: {
            jobId: payload.jobId,
            data: response,
          },
        }),
      ]),
      catchError(error => [JobMatchingPlacementsActions.generateFailed({ payload: { error } })]),
    ),
  );

  public dismiss$ = this.effects(JobMatchingPlacementsActions.dismiss, ({ payload }) =>
    this.http.dismiss$(payload.jobId, payload.matchId).pipe(
      switchMap(() => [
        JobMatchingPlacementsActions.dismissSuccess({
          payload: { jobId: payload.jobId, matchId: payload.matchId },
        }),
      ]),
      catchError(error => [
        JobMatchingPlacementsActions.dismissFailed({
          payload: { jobId: payload.jobId, error },
        }),
        JobMatchingPlacementsActions.fetchOne({ payload: { jobId: payload.jobId } }),
      ]),
    ),
  );

  public assign$ = this.effects(JobMatchingPlacementsActions.assign, ({ payload }) =>
    this.http.assign$(payload.jobId, payload.matchId, payload.payload).pipe(
      switchMap(response => [
        JobMatchingPlacementsActions.assignSuccess({
          payload: { jobId: payload.jobId, matchId: payload.matchId, response },
        }),
      ]),
      catchError(error => [
        JobMatchingPlacementsActions.assignFailed({
          payload: { jobId: payload.jobId, error },
        }),
        JobMatchingPlacementsActions.fetchOne({ payload: { jobId: payload.jobId } }),
      ]),
    ),
  );

  public bulkAssign$ = this.effects(JobMatchingPlacementsActions.bulkAssign, ({ payload }) =>
    this.http.bulkAssign$(payload.jobId).pipe(
      switchMap(({ uuid }) => [
        AsyncJobActions.create({
          payload: { job: { uuid, type: AsyncJobType.BulkAssignCandidateMatching } },
        }),
        JobMatchingPlacementsActions.bulkAssignSuccess({
          payload: { jobId: payload.jobId, data: { uuid } },
        }),
      ]),
      catchError(error => [
        JobMatchingPlacementsActions.bulkAssignFailed({
          payload: { jobId: payload.jobId, error },
        }),
        JobMatchingPlacementsActions.fetchOne({ payload: { jobId: payload.jobId } }),
      ]),
    ),
  );
}
